import { useMemo } from "react";
import { CheckCircle2, CloudOff, Database, FileSpreadsheet, Link2Off, ShieldCheck, UploadCloud, XCircle } from "lucide-react";
import { PageHeader } from "../components/Layout";
import { InfoTip } from "../components/Erklaerung";
import { Badge, Card } from "../components/ui";
import { useApp } from "../../store/AppStore";
import { formatNum, formatPct } from "../../domain/rules";
import { ConnectorSource, validateImportRow, type RawRow } from "../../import/ImportSource";
import { PCE_VERSION, PCE_TOTAL_RULES } from "../../data/pce-ruleset";

// Kerntabellen Nacht 1 (§7.1) — als Statusliste im Upload-Feld.
const CORE_TABLES = [
  { name: "Benutzerstamm", detail: "Konten, System, Mandant, Anmeldedaten" },
  { name: "Rollendefinition", detail: "Einzel- und Sammelrollen je System" },
  { name: "Rollenzuordnung", detail: "Welche Rolle hängt an welchem Konto" },
  { name: "Berechtigungswerte", detail: "Objekte, Felder, Werte (der Treiber)" },
  { name: "Gepflegter Lizenztyp", detail: "Im System gepflegte Klasse je Konto" },
  { name: "Nutzungstrace", detail: "je Konto und Wert: genutzt / nie genutzt / kein Trace, letzte Nutzung (nutzung.csv)" },
  { name: "Verwendungsdaten", detail: "Transaktion, Programm, letzte Verwendung je Konto und Wert (nutzung_detail.csv)" },
];

// Spalten-Mapping (Demo): Rohspalte → Zielfeld, mit Autoerkennung/manueller Korrektur.
const MAPPING = [
  { raw: "BNAME", field: "Benutzername", mode: "auto" as const },
  { raw: "MANDT", field: "Mandant", mode: "auto" as const },
  { raw: "SID", field: "Systemkennung", mode: "auto" as const },
  { raw: "PERNR", field: "Personalnummer", mode: "auto" as const },
  { raw: "SMTP_ADDR", field: "E-Mail", mode: "manuell" as const },
  { raw: "AGR_NAME", field: "Rolle", mode: "auto" as const },
  { raw: "LIC_TYPE", field: "Gepflegter Lizenztyp", mode: "manuell" as const },
  { raw: "TCODE", field: "Transaktion", mode: "auto" as const },
  { raw: "LAST_USED", field: "letzte Verwendung", mode: "auto" as const },
  { raw: "FUNCTION", field: "Funktion (SU01)", mode: "auto" as const },
  { raw: "DEPARTMENT", field: "Abteilung (SU01)", mode: "auto" as const },
];

// Pflichtfeldprüfung (I15): drei Beispielzeilen durch validateImportRow.
const SAMPLE_ROWS: { label: string; row: RawRow }[] = [
  { label: "vollständig", row: { SID: "GP7", MANDT: "100", BNAME: "MUELLERA" } },
  { label: "ohne Systemkennung", row: { SID: "", MANDT: "100", BNAME: "SCHMIDTB" } },
  { label: "ohne Mandant", row: { SID: "CS7", MANDT: "", BNAME: "KAISERC" } },
];

// §10 — bewusst nicht gebaut: graue, benannte Kacheln.
const NOT_CONNECTED = [
  { title: "BTP / CPEA", detail: "Cloud-Verbrauch (Consumption) — eigene Metrik, hier nicht abgebildet." },
  { title: "Power BI", detail: "Externes Reporting — keine Anbindung." },
  { title: "Echte Systemanbindung", detail: "Direkter RFC-/OData-Abruf aus den Produktivsystemen." },
  { title: "Altes nutzungsbasiertes Lizenzmodell", detail: "Vor-RISE-Metrik — nicht Teil dieses Werkzeugs." },
  { title: "PFCG-Exit", detail: "Rückschreiben von Rollenänderungen ins System." },
  { title: "Monitoring / Alerting", detail: "Laufende Überwachung und Benachrichtigungen." },
  { title: "Governance-Sicht", detail: "Rollen-Ownership, Rezertifizierung, Antragsworkflows." },
];

export function Import() {
  const { ds } = useApp();

  // K4 aus den tatsächlichen Beispieldaten gerechnet — keine erfundenen Zahlen.
  const quality = useMemo(() => {
    const fachlich = ds.accounts.filter((a) => a.personId.startsWith("P-"));
    const total = fachlich.length || 1;
    const withPn = fachlich.filter((a) => a.personalnummer && a.personalnummer.trim() !== "").length;
    const withEmail = fachlich.filter((a) => a.email && a.email.trim() !== "").length;
    // Pflichtfelder (SID/MANDT/BNAME) sind konstruktionsbedingt vollständig.
    const rules = [
      { name: "Pflichtfelder vollständig", detail: "System-, Mandantenkennung, Benutzername", ok: total, total },
      { name: "Personalnummer vorhanden", detail: "Voraussetzung für sauberste Zusammenführung", ok: withPn, total },
      { name: "E-Mail vorhanden", detail: "alternativer Identitätsschlüssel", ok: withEmail, total },
    ];
    const okSum = rules.reduce((a, r) => a + r.ok, 0);
    const score = (okSum / (rules.length * total)) * 100; // Konfidenzgrad K4
    return { total, rules, score };
  }, [ds]);

  return (
    <div>
      <PageHeader route="/import" />

      {/* K4 — Datenqualität & Konfidenz */}
      <div className="mb-6">
        <Card
          title="Datenqualität & Konfidenz (K4)"
          subtitle="Ziel ≥ 95 %"
          right={<Badge tone={quality.score >= 95 ? "green" : "amber"}>{formatPct(quality.score, 1)}</Badge>}
        >
          <div className="mb-3 flex items-center gap-2">
            <ShieldCheck size={18} style={{ color: quality.score >= 95 ? "var(--color-amp-green)" : "var(--color-amp-amber)" }} />
            <span className="tnum text-strong text-2xl font-semibold">{formatPct(quality.score, 1)}</span>
            <span className="text-faint text-xs">Konfidenzgrad über {formatNum(quality.total)} fachliche Konten (von {formatNum(ds.accounts.length)} gesamt, ohne {formatNum(ds.technicalCount)} technische)</span>
          </div>
          <div className="space-y-2">
            {quality.rules.map((r) => {
              const pct = (r.ok / r.total) * 100;
              return (
                <div key={r.name}>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-normal">{r.name}</span>
                    <span className="tnum text-dim">{formatPct(pct, 1)}</span>
                  </div>
                  <div className="bg-sunken mt-1 h-2 w-full overflow-hidden rounded-full">
                    <div className="h-2 rounded-full" style={{ width: `${pct}%`, backgroundColor: pct >= 95 ? "var(--color-amp-green)" : "var(--color-amp-amber)" }} />
                  </div>
                  <div className="text-faint mt-0.5 text-xs">{r.detail} · {formatNum(r.ok)} von {formatNum(r.total)}</div>
                </div>
              );
            })}
          </div>
          <div className="text-faint mt-3 flex items-center gap-1.5 text-xs">
            <ShieldCheck size={13} className="shrink-0" /> PCE-Regelwerk {PCE_VERSION} · {formatNum(PCE_TOTAL_RULES)} Regeln
          </div>
        </Card>
      </div>

      {/* Zwei Quellen */}
      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title="Upload" subtitle="Demo · kein echter Upload" right={<Badge tone="green">aktiv</Badge>}>
          <div
            className="flex flex-col items-center justify-center rounded-xl px-6 py-8 text-center"
            style={{ borderWidth: 2, borderStyle: "dashed", borderColor: "var(--border-strong)" }}
          >
            <UploadCloud size={32} className="text-faint" />
            <div className="text-strong mt-2 text-sm font-semibold">Dateien hierher ziehen oder auswählen</div>
            <div className="text-faint mt-1 text-xs">CSV und XLSX · gestreamt verarbeitet, mit Fortschrittsanzeige</div>
          </div>
          <div className="mt-4">
            <div className="text-faint mb-2 text-xs font-medium uppercase tracking-wide">Erwartete Kerntabellen</div>
            <ul className="space-y-1.5">
              {CORE_TABLES.map((t) => (
                <li key={t.name} className="flex items-start gap-2 text-sm">
                  <Database size={14} className="text-faint mt-0.5 shrink-0" />
                  <span>
                    <span className="text-strong font-medium">{t.name}</span>
                    <span className="text-faint"> — {t.detail}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </Card>

        <Card title={ConnectorSource.label} subtitle="automatischer Abruf aus den Systemen" right={<Badge tone="gray">geplant</Badge>}>
          <div
            className="flex flex-col items-center justify-center rounded-xl px-6 py-8 text-center opacity-70"
            style={{ borderWidth: 2, borderStyle: "dashed", borderColor: "var(--border-soft)", backgroundColor: "var(--surface-sunken)" }}
          >
            <CloudOff size={32} className="text-faint" />
            <div className="text-dim mt-2 text-sm font-semibold">Noch nicht angebunden</div>
            <div className="text-faint mt-1 text-xs">
              Quelle <span className="font-mono">{ConnectorSource.id}</span> · available: {String(ConnectorSource.available)}
            </div>
          </div>
          <div className="text-faint mt-4 inline-flex items-center gap-1 text-xs">gleicher Verarbeitungspfad <InfoTip>Die Importschicht ist als Interface gebaut: ein automatischer Konnektor läuft über denselben Verarbeitungspfad wie der Upload.</InfoTip></div>
        </Card>
      </div>

      {/* Spalten-Mapping */}
      <Card
        title="Spalten-Mapping (Demo)"
        subtitle={<span className="inline-flex items-center gap-1">Rohspalte → Zielfeld <InfoTip>Rohspalten werden automatisch erkannt und auf Zielfelder gemappt. Abweichende Spalten werden manuell korrigiert.</InfoTip></span>}
        className="mb-6"
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] border-collapse text-sm">
            <thead>
              <tr className="text-faint text-left text-xs uppercase tracking-wide">
                <th className="px-3 py-2 font-medium">Rohspalte</th>
                <th className="px-3 py-2 font-medium"></th>
                <th className="px-3 py-2 font-medium">Zielfeld</th>
                <th className="px-3 py-2 font-medium">Erkennung</th>
              </tr>
            </thead>
            <tbody>
              {MAPPING.map((m) => (
                <tr key={m.raw} style={{ borderTopWidth: 1, borderColor: "var(--border-soft)" }}>
                  <td className="text-normal px-3 py-2.5 font-mono text-[13px]">{m.raw}</td>
                  <td className="text-faint px-3 py-2.5">→</td>
                  <td className="text-strong px-3 py-2.5 font-medium">{m.field}</td>
                  <td className="px-3 py-2.5">
                    <Badge tone={m.mode === "auto" ? "green" : "amber"}>
                      {m.mode === "auto" ? "automatisch" : "manuell korrigiert"}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pflichtfeldprüfung I15 */}
      <Card
        title="Pflichtfeldprüfung"
        subtitle="System- und Mandantenkennung sind Pflicht"
        className="mb-6"
      >
        <div className="space-y-2">
          {SAMPLE_ROWS.map((s, i) => {
            const res = validateImportRow(s.row);
            return (
              <div
                key={i}
                className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg px-4 py-3"
                style={{
                  borderWidth: 1,
                  borderColor: res.ok ? "var(--color-amp-green)" : "var(--color-amp-red)",
                  backgroundColor: "var(--surface-sunken)",
                }}
              >
                {res.ok ? (
                  <CheckCircle2 size={18} className="shrink-0" style={{ color: "var(--color-amp-green)" }} />
                ) : (
                  <XCircle size={18} className="shrink-0" style={{ color: "var(--color-amp-red)" }} />
                )}
                <span className="text-normal font-mono text-[13px]">
                  SID=<span className="text-strong">{s.row.SID || "∅"}</span> · MANDT=
                  <span className="text-strong">{s.row.MANDT || "∅"}</span> · BNAME=
                  <span className="text-strong">{s.row.BNAME}</span>
                </span>
                <span className="text-faint text-xs">({s.label})</span>
                <span className="ml-auto text-sm font-semibold" style={{ color: res.ok ? "var(--color-amp-green)" : "var(--color-amp-red)" }}>
                  {res.ok ? "übernommen" : `abgelehnt — ${res.reason}`}
                </span>
              </div>
            );
          })}
        </div>
        <div className="text-faint mt-3 flex items-center gap-1.5 text-xs">
          <FileSpreadsheet size={13} /> Kennung aus Spalte oder Dateiname <InfoTip>Fehlt System- oder Mandantenkennung, wird die Zeile abgelehnt.</InfoTip>
        </div>
      </Card>

      {/* §10 — noch nicht angebunden */}
      <Card title="Noch nicht angebunden" subtitle="bewusst nicht gebaut · keine erfundenen Zahlen">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {NOT_CONNECTED.map((n) => (
            <div
              key={n.title}
              className="rounded-xl px-4 py-3 opacity-80"
              style={{ borderWidth: 1, borderStyle: "dashed", borderColor: "var(--border-soft)", backgroundColor: "var(--surface-sunken)" }}
            >
              <div className="text-dim flex items-center gap-2 text-sm font-semibold">
                <Link2Off size={15} className="text-faint shrink-0" />
                {n.title}
              </div>
              <p className="text-faint mt-1 text-xs">{n.detail}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
